import { Profile } from '@/contexts/auth/types';
import { User } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';

/**
 * Append a version query param in a safe way (handles existing queries)
 */
function withVersion(url: string, version: number | string) {
  if (!url) return url;
  const sep = url.includes('?') ? '&' : '?';
  return `${url}${sep}v=${version}`;
}

/**
 * Normalize avatarUpdatedAt into a numeric version (falls back to Date.now())
 */
function versionFromDate(avatarUpdatedAt?: string | null) {
  const t = avatarUpdatedAt ? Date.parse(avatarUpdatedAt) : NaN;
  return Number.isFinite(t) ? t : Date.now();
}

/**
 * Generate a robust, cache-busted avatar URL.
 * - Prefers storage path → falls back to provider/legacy URL
 * - Returns `undefined` when no URL is available (avoids <img src=""> issues)
 * - Always cache-busts using avatarUpdatedAt (or now() if missing)
 */
export const avatarUrlFromPath = (
  avatarPath?: string | null,
  fallbackUrl?: string | null,
  avatarUpdatedAt?: string | null
): string | undefined => {
  try {
    const ver = versionFromDate(avatarUpdatedAt);

    if (avatarPath) {
      const { data } = supabase.storage.from('user-avatars').getPublicUrl(avatarPath);
      const base = data?.publicUrl;
      return base ? withVersion(base, ver) : undefined;
    }

    if (fallbackUrl && fallbackUrl.trim().length > 0) {
      return withVersion(fallbackUrl, ver);
    }

    return undefined;
  } catch {
    // Never throw from a rendering helper
    return undefined;
  }
};

/**
 * Creates a safe fallback profile object to prevent null reference errors.
 * Ensures components always receive a valid profile object with a computed avatar URL.
 */
export const createFallbackProfile = (user: User, profile: Profile | null): Profile => {
  // Compute avatar URL properly using path and cache-busting (may be undefined)
  const computedAvatarUrl =
    profile
      ? avatarUrlFromPath(profile.avatar_path, profile.avatar_url, profile.avatar_updated_at)
      : undefined;

  // If nothing available, fall back to identicon (no network errors if email missing)
  const email = (user.email || '').trim().toLowerCase();
  const defaultIdenticon = `https://www.gravatar.com/avatar/${btoa(email)}?d=identicon&s=200`;

  const baseProfile: Profile = {
    id: user.id,
    email: user.email || '',
    username: profile?.username || user.email?.split('@')[0] || 'user',
    first_name: profile?.first_name || '',
    last_name: profile?.last_name || '',
    full_name:
      profile?.full_name ||
      (profile?.first_name && profile?.last_name
        ? `${profile.first_name} ${profile.last_name}`
        : profile?.username || user.email?.split('@')[0] || 'User'),
    avatar_url: computedAvatarUrl || defaultIdenticon,
    avatar_path: profile?.avatar_path || null,
    avatar_updated_at: profile?.avatar_updated_at || null,
    bio: profile?.bio || '',
    role_text: profile?.role_text || 'free',
    phone: profile?.phone || '',
    created_at: profile?.created_at || new Date().toISOString(),
    updated_at: profile?.updated_at || new Date().toISOString(),
    group_name: profile?.group_name || '',
    church_name: profile?.church_name || '',
    is_system_account: profile?.is_system_account || false,
    followers_count: profile?.followers_count || 0,
    following_count: profile?.following_count || 0,
    discord_id: profile?.discord_id || '',
    discord_username: profile?.discord_username || '',
    discord_avatar_url: profile?.discord_avatar_url || '',
  };

  // Merge incoming profile to preserve any fields it already has,
  // but keep `avatar_url` as our computed/cache-busted value.
  if (profile) {
    return { ...baseProfile, ...profile, avatar_url: baseProfile.avatar_url };
  }

  return baseProfile;
};
